import React, { useMemo } from 'react';
import { useIntl, getLocale } from 'umi';
import { useMemoizedFn } from 'ahooks';
import { Button } from 'tea-component';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import NoEntityCard from '@/pages/intelligence/pages/Compare/components/NoEntityCard';
import EntitiesMetricsCompareTable from '../../components/EntitiesMetricsCompareTable';
import EntitiesMetricsCumulativeCompareTable from '../../components/EntitiesMetricsCompareTable/components/EntitiesMetricsCumulativeCompareTable';
import { BaseLineChart } from '../../components/ECharts';
import Actionbar from '../../../components/Actionbar';
import DefaultHeaderColumnRenderer from './renderers/HeaderColumnRenderer';
// import useChartInfo from './hooks/useChartInfo';
import useChartInfoNew from './hooks/useChartInfoNew';
import {
  metricValueFormatter,
  metricGameSerieKeyFormatter,
  createDateFormatter,
  addFormatMessageSafelyHandler,
} from '../../utils';
import { analyseDataSourceMetricKey } from '../../utils/dataSourcesMetrics';
import {
  PERIODS_NAMES_MAPPING,
  FLATTEN_METRICS_NAMES_MAPPING,
  FLATTEN_METRICS_NAMES_TOOLTIPS_MAPPING,
  FLATTEN_METRICS_MODES_MAPPING,
  METRICS_CALC_MODES_NAMES_MAPPING,
} from '../../constants';

import type { IPCConsoleGamesCumulativeMetric, IPCConsoleGame } from '@/pages/intelligence/types/pcConsoleCompare';
import type { IEntityCumulativeMetricDataGetter } from '../../components/EntitiesMetricsCompareTable/components/EntitiesMetricsCumulativeCompareTable/types';
import type { ICompareResultProps } from './types';

function createCumulativeMetricDataDefaultGetter(map?: {
  [key: string]: IPCConsoleGamesCumulativeMetric[];
}): IEntityCumulativeMetricDataGetter {
  return (game, metric) => {
    const result = {
      mode: '',
      value: '',
    };
    const gameId = game?.gameId;
    if (!gameId) return result;
    const cumulativeMetricsData = map?.[gameId];
    if (!Array.isArray(cumulativeMetricsData)) return result;
    const cumulativeMetricData = cumulativeMetricsData.find((item) => {
      const { metricKey } = analyseDataSourceMetricKey(metric);
      return item?.source === metricKey;
    });
    if (!cumulativeMetricData) return result;
    result.mode = cumulativeMetricData.mode || '';
    result.value = cumulativeMetricData.value || '';
    return result;
  };
}

export default function CompareResult(props: ICompareResultProps) {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const formatMessageSafely = addFormatMessageSafelyHandler(formatMessage);

  const {
    pageKey,
    isLoading,
    isVisualisLoading,
    isVisualisNoData,
    isHeatMap = false,
    isShowTopCountries = false,
    isOnlySingleMetricLine = false,
    isShowTitleTips = false,
    titleTips = '',
    periods,
    metrics,
    games,
    dateTypes,
    devices,
    fieldsMappings,

    isDownloading,
    setIsDownloadVisible,

    headerColumnRenderer: headerColumnRendererOutter,
    rangePickerProps,
    composedDateData,
    oncomposedDateDataChange,

    tablesProps,

    trendsProps,
  } = props;

  const trendsAbnormalHeight = (() => {
    if (isVisualisLoading) return 400;
    if (isVisualisNoData) return 200;
    return 'auto';
  })();

  const {
    entityFieldsMapping = {
      entityKey: 'gameId',
      entityName: 'gameName',
      entityCover: 'cover',
    },
    metricFieldsMapping = {
      entityKey: '',
      period: '',
      metricKey: 'metric',
      metricName: '',
      metricValue: 'value',
      countries: 'countrys',
    },
  } = fieldsMappings || {};

  const headerColumnRenderer =
    typeof headerColumnRendererOutter === 'function' ? headerColumnRendererOutter : DefaultHeaderColumnRenderer;

  const headerColumnRendererPlus = (entity: IPCConsoleGame, columnIndex: number) =>
    headerColumnRenderer(pageKey, entity, columnIndex, { devices });

  const {
    tablesMetricsMap,
    tablesPeriodMaxMetricsMap,
    onAddGames,
    onDeleteGames,
    clearAllBtnText = formatMessage({ id: 'dbi_compare_clear_all' }),
    onClearAllGames,
  } = tablesProps || {};

  const {
    tooltipTitle,
    tooltipOption = {
      enterable: true,
    },
    trendsDimensions,
    trendsXAxisData,
    trendsData,
    trendsCumulativeMetricsDataMap,
    getEntityCumulativeMetricData = createCumulativeMetricDataDefaultGetter(trendsCumulativeMetricsDataMap),
  } = trendsProps || {};
  // const { chartComp, chartColors, chartNodeHeight, chartLegendOptions } = useChartInfo({
  //   isLoading: isLoading || isVisualisLoading,
  //   dimensions: trendsDimensions.slice(1),
  //   // 每行最多几列，即游戏的数量
  //   cellsCountPerRow: games?.length,
  //   // 每列最多几行，即指标的数量
  //   rowsCountPerCell: metrics?.length,
  // });

  const { chartComp, chartColors, chartNodeHeight, chartLegendOptions } = useChartInfoNew({
    isLoading: isLoading || isVisualisLoading,
    dimensions: trendsDimensions.slice(1),
    // 游戏的数量
    gamesLength: games?.length,
    // 指标的数量
    metricsLength: metrics?.length,
  });

  const metricGameNameFormatter = useMemoizedFn((name: string): string => {
    const game = games?.find((item) => {
      const itemGameId = item?.gameId;
      return itemGameId && name?.endsWith(itemGameId);
    });
    const gameId = game?.gameId || '';
    const gameName = game?.name || '';
    if (metrics?.length === 1) return gameName || '-';
    const nameTemp = name?.replace(gameId, gameName) || '';
    return metricGameSerieKeyFormatter(nameTemp, { translater: formatMessageSafely }) || '-';
  });

  const visualisFormatters = useMemo(
    () => ({
      serieNameFormatter: metricGameNameFormatter,
      xAxisLabelFormatter: createDateFormatter(composedDateData.mode, composedDateData.dateType),
      yAxisLabelFormatter: metricValueFormatter,
    }),
    [composedDateData.mode, composedDateData.dateType, metricGameNameFormatter],
  );

  return (
    <>
      {/* 加载动画 */}
      {isLoading && (
        <>
          <Abnormal
            // style={{ margin: '0 0 64px 0', height: 482, backgroundColor: '#fff', borderRadius: 8 }}
            style={{ margin: '0 0 20px 0', height: 482, backgroundColor: '#fff', borderRadius: 8 }}
            isLoading={true}
          />
          <Abnormal style={{ height: 400, backgroundColor: '#fff', borderRadius: 8 }} isLoading={true} />
        </>
      )}
      {isLoading || (
        <>
          {/* 无数据 */}
          {games?.length > 0 || <NoEntityCard onAdd={onAddGames} />}
          {games?.length > 0 && (
            <CardSlot
              title={
                <div className="dbi-pc-console-compare-title">
                  <span className="dbi-pc-console-compare-title-text">{formatMessage({ id: 'dbi_games' })}</span>
                  {isShowTitleTips && (
                    <TipContext className="tip-icon" AWidth={555} placement="right" tipText={titleTips} />
                  )}
                </div>
              }
              className="dbi-pc-console-compare-card"
              iconList={[]}
            >
              <div className="dbi-pc-console-compare-content">
                {/* 游戏、阶段、指标 对比表格 */}
                <EntitiesMetricsCompareTable
                  isOpenHeatMap={isHeatMap}
                  isShowTopCountries={isShowTopCountries}
                  isOnlySingleMetricLine={isOnlySingleMetricLine}
                  entities={games}
                  entityFieldsMapping={entityFieldsMapping}
                  headerColumnRenderer={headerColumnRendererPlus}
                  periods={periods}
                  periodsNamesMapping={PERIODS_NAMES_MAPPING}
                  metrics={metrics}
                  metricsNamesMapping={FLATTEN_METRICS_NAMES_MAPPING}
                  metricsNamesTooltipMapping={FLATTEN_METRICS_NAMES_TOOLTIPS_MAPPING}
                  metricFieldsMapping={metricFieldsMapping}
                  metricsMap={tablesMetricsMap}
                  defaultMetricValueFormatter={metricValueFormatter}
                  periodMaxMetricsMap={tablesPeriodMaxMetricsMap}
                  onAdd={onAddGames}
                  onDelete={onDeleteGames}
                />
                {/* 时间筛选器、下载按钮 操作栏 */}
                <Actionbar
                  rangePickerProps={rangePickerProps}
                  disabled={isLoading || isVisualisLoading}
                  dateTypes={dateTypes}
                  composedDateData={composedDateData}
                  onComposedPickerChange={oncomposedDateDataChange}
                  downloadNodeRenderer={() => {
                    return (
                      <Button
                        icon="download"
                        className="ns-btn--icon"
                        title="Download"
                        disabled={isLoading || isVisualisLoading}
                        loading={isDownloading}
                        onClick={() => {
                          setIsDownloadVisible?.(true);
                        }}
                      />
                    );
                  }}
                />
                <Abnormal
                  style={{ height: trendsAbnormalHeight }}
                  isLoading={isVisualisLoading}
                  isNoData={isVisualisNoData}
                  component={
                    <>
                      {/* 游戏指标聚合值对比表格 */}
                      <EntitiesMetricsCumulativeCompareTable
                        entities={games}
                        metrics={metrics}
                        metricsNamesMapping={FLATTEN_METRICS_NAMES_MAPPING}
                        metricsCalcModesMapping={FLATTEN_METRICS_MODES_MAPPING}
                        metricsCalcModesNamesMapping={METRICS_CALC_MODES_NAMES_MAPPING}
                        defaultMetricValueFormatter={metricValueFormatter}
                        getEntityCumulativeMetricData={getEntityCumulativeMetricData}
                      />
                      <BaseLineChart
                        ref={chartComp}
                        locale={locale}
                        chartNodeHeight={chartNodeHeight}
                        color={chartColors}
                        tooltipTitle={tooltipTitle}
                        tooltipOption={tooltipOption}
                        legendOption={chartLegendOptions}
                        dimensions={trendsDimensions}
                        xAxisData={trendsXAxisData}
                        sources={trendsData}
                        formatters={visualisFormatters}
                      />
                    </>
                  }
                />
              </div>
              <span className="dbi-pc-console-compare-clear-btn" onClick={onClearAllGames}>
                {clearAllBtnText}
              </span>
            </CardSlot>
          )}
        </>
      )}
    </>
  );
}
